
import React, { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { Loader2 } from 'lucide-react';

interface FloorPlanPreviewProps {
  id: string;
  name: string;
  previewImage: string | null | undefined;
  allowRefresh?: boolean;
  className?: string;
}

export function FloorPlanPreview({ id, name, previewImage, allowRefresh = true, className }: FloorPlanPreviewProps) {
  const [isImageLoading, setIsImageLoading] = useState(!!previewImage);
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Reset loading state when previewImage changes
  useEffect(() => {
    if (previewImage) {
      setIsImageLoading(true);
      setImageError(false);
      setRetryCount(0); // Reset retry counter when a new image is provided
    }
  }, [previewImage]);

  const handleImageLoad = () => {
    console.log(`Preview image for ${id} loaded successfully`);
    setIsImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    console.error(`Error loading preview image for ${id}`);
    setIsImageLoading(false);
    setImageError(true);

    // Auto-retry loading a few times with exponential backoff
    if (retryCount < 3 && previewImage) {
      console.log(`Retrying preview image load (${retryCount + 1}/3) for ${id}`);
      const backoffTime = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
      
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setIsImageLoading(true);
        setImageError(false);
      }, backoffTime);
    }
  };

  return (
    <div 
      className={cn(
        "aspect-video w-full rounded-md overflow-hidden border bg-muted relative",
        className
      )}
    >
      {previewImage ? (
        <>
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          <img
            src={previewImage}
            alt={`Voorvertoning van ${name}`}
            className={cn(
              "w-full h-full object-contain", // Veranderd van object-cover naar object-contain om ervoor te zorgen dat de volledige plattegrond zichtbaar is
              isImageLoading && "opacity-0"
            )}
            data-preview-id={id}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          {imageError && retryCount >= 3 && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/80 text-muted-foreground text-sm text-center px-2">
              Kon voorvertoning niet laden
            </div>
          )}
        </>
      ) : (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
          {imageError ? "Fout bij laden van voorvertoning" : "Geen voorvertoning beschikbaar"}
        </div>
      )}
    </div>
  );
}
